import type { MarkingSection } from '#shared/utils/markings/schema'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import { findCitationParent } from './text-filter'
import { readSectionPath } from './section-metadata'

const headingSelector = 'h1, h2, h3, h4, h5, h6'

/**
 * TOC titles come from Solr with their source line breaks («KAPITTEL 1\nInnføring
 * …»), headings from `textContent`. Both render as one line, so both are
 * normalized — otherwise the same section is stored as two different entries.
 */
function normalizeTitle(title: string | null | undefined): string {
  return (title ?? '').replace(/\s+/g, ' ').trim()
}

function precedesSelection(element: Element, target: Node) {
  return element.contains(target) || Boolean(element.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)
}

function headingPath(root: Element, target: Node): MarkingSection[] {
  const headings: Element[] = []

  for (const heading of root.querySelectorAll(headingSelector)) {
    if (findCitationParent(heading) !== root || !precedesSelection(heading, target)) {
      continue
    }

    const level = Number(heading.tagName.slice(1))

    while (headings.length && Number(headings[headings.length - 1]!.tagName.slice(1)) >= level) {
      headings.pop()
    }

    headings.push(heading)
  }

  return headings.flatMap(heading => {
    const title = normalizeTitle(heading.textContent)
    return title ? [{ kind: 'heading' as const, title, anchor: heading.id || undefined }] : []
  })
}

function tocPaths(items: TocItemData[], parents: TocItemData[] = []): TocItemData[][] {
  return items.flatMap(item => {
    const path = [...parents, item]
    return [path, ...tocPaths(item.children ?? [], path)]
  })
}

function sharedParents(paths: TocItemData[][]): TocItemData[] {
  const first = paths[0] ?? []
  return first.filter((item, index) => paths.every(path => path[index] === item))
}

function numericPage(page?: string | null): number | undefined {
  return page && /^\d+$/.test(page) ? Number(page) : undefined
}

function bookTocPath(root: Element, target: Node, items: TocItemData[]): MarkingSection[] {
  const paths = tocPaths(items)
  const targetElement = target instanceof Element ? target : target.parentElement
  const page = numericPage(targetElement?.closest('[data-citation-page]')?.getAttribute('data-citation-page'))
  const anchors = Array.from(root.querySelectorAll('[id]'))
  const candidates = paths.filter(path => {
    const item = path[path.length - 1]!
    const startPage = numericPage(item.pageNumberString)
    const anchor = item.fragmentId ? anchors.find(element => element.id === item.fragmentId) : undefined

    if (anchor && !precedesSelection(anchor, target)) {
      return false
    }

    return page !== undefined && startPage !== undefined && startPage <= page
  })
  const latestPage = Math.max(...candidates.map(path => numericPage(path[path.length - 1]?.pageNumberString) ?? -1))
  let matchingPaths = candidates.filter(path => numericPage(path[path.length - 1]?.pageNumberString) === latestPage)

  // Exact anchors on the selected page disambiguate sections sharing a page number.
  for (const element of anchors) {
    if (!precedesSelection(element, target)) {
      continue
    }

    const elementPage = numericPage(element.closest('[data-citation-page]')?.getAttribute('data-citation-page'))
    const anchored = paths.filter(path => {
      const item = path[path.length - 1]!
      const startPage = numericPage(item.pageNumberString)
      return item.fragmentId === element.id && (startPage === undefined || startPage === elementPage)
    })

    if (anchored.length && (elementPage === undefined || elementPage >= latestPage)) {
      matchingPaths = anchored
    }
  }

  // A parent and its first child can share a start. Prefer the deepest known path.
  const deepestPaths = matchingPaths.filter(path => !matchingPaths.some(other =>
    other.length > path.length && path.every((item, index) => other[index] === item),
  ))
  return sharedParents(deepestPaths).map(item => ({
    kind: 'toc',
    title: normalizeTitle(item.title),
    href: item.href || undefined,
    anchor: item.fragmentId || undefined,
  }))
}

export function captureSectionPath(range: Range, tocItems: TocItemData[] = []): MarkingSection[] {
  const root = findCitationParent(range.startContainer)

  if (!root) {
    return []
  }

  const contentType = root.getAttribute('data-marking-content-type')
  const documentId = root.getAttribute('data-marking-document-id')
  const path: MarkingSection[] = contentType === 'book' ? bookTocPath(root, range.startContainer, tocItems) : readSectionPath(root)
  const units: MarkingSection[] = []
  let unit: Element | null = root

  while (unit && unit.getAttribute('data-marking-document-id') === documentId && unit.getAttribute('data-marking-content-type') === contentType) {
    const title = normalizeTitle(unit.getAttribute('data-marking-unit-title'))
    const unitId = unit.getAttribute('data-marking-unit-id')

    if (title && unitId) {
      units.unshift({
        kind: 'unit',
        nodeType: unit.getAttribute('data-marking-node-type') || undefined,
        title,
        unitId,
        anchor: unit.id || undefined,
      })
    }

    unit = unit.parentElement?.closest('[data-marking-unit-id]') ?? null
  }

  for (const section of [...units, ...headingPath(root, range.startContainer)]) {
    const duplicate = path.findIndex(existing =>
      (section.unitId && existing.unitId === section.unitId) || (section.anchor && existing.anchor === section.anchor) || (existing === path[path.length - 1] && existing.title === section.title),
    )

    if (duplicate >= 0) {
      continue
    }

    path.push(section)
  }

  return path
}
